export const DELETED_ACCOUNT_MESSAGE =
  "An account using this sign-in method was previously closed. To prevent abuse, new accounts cannot be created with the same sign-in method. If you believe this is an error, contact support."

export const EMAIL_DELIVERABILITY_REJECTION_MESSAGE =
  "This email address could not be verified. Please use a valid, deliverable email."

export const EMAIL_VERIFICATION_REJECTION_MESSAGE =
  "We couldn't verify this email. Please use a different address."

export const EMAIL_BLACKLIST_REJECTION_MESSAGE =
  "We don't accept temporary or disposable email addresses. Please use a personal or work email."

export const DISPOSABLE_EMAIL_REJECTION_MESSAGE =
  "Disposable or temporary email addresses are not allowed."

export const EMAIL_VERIFICATION_UNAVAILABLE_MESSAGE =
  "We couldn't verify this email right now. Please try again later."

export const SIGNUP_UNAVAILABLE_MESSAGE =
  "We couldn't create your account right now. Please try again in a few minutes."

/**
 * These are intentional signup checks, not credentials failures. Keeping the
 * distinction lets the UI keep people on the form where they can correct the
 * address instead of sending them to a sign-in flow with no account.
 */
export function isCorrectableEmailRejection(message: string | null): boolean {
  return (
    message === EMAIL_DELIVERABILITY_REJECTION_MESSAGE ||
    message === EMAIL_VERIFICATION_REJECTION_MESSAGE ||
    message === EMAIL_BLACKLIST_REJECTION_MESSAGE ||
    message === DISPOSABLE_EMAIL_REJECTION_MESSAGE
  )
}

const WEAK_PASSWORD_MESSAGE =
  "That password is too easy to guess. Please choose a stronger password."

const ACCOUNT_EXISTS_MESSAGE =
  "An account with this email already exists. Try signing in instead."

const SIGNUP_RATE_LIMIT_MESSAGE =
  "Too many sign-up attempts. Please wait a few minutes and try again."

const GENERIC_SIGNUP_ERROR =
  "Something went wrong. Please try again or use a different email."

type SignUpErrorLike = {
  code?: string
  message?: string
  status?: number
}

export function getSignUpErrorMessage(error: SignUpErrorLike): string {
  const code = error.code?.toLowerCase()
  const message = error.message ?? ""
  const normalized = message.toLowerCase()

  if (
    code === "weak_password" ||
    normalized.includes("known to be weak") ||
    (normalized.includes("password") && normalized.includes("easy to guess"))
  ) {
    return WEAK_PASSWORD_MESSAGE
  }

  if (
    code === "user_already_exists" ||
    code === "email_exists" ||
    message.includes("already registered")
  ) {
    return ACCOUNT_EXISTS_MESSAGE
  }

  if (message.includes("previously closed")) {
    return DELETED_ACCOUNT_MESSAGE
  }

  // The before-user-created Auth hook rejects with one of these messages and
  // no error code (status 400 or 429), so match it before the status rules.
  if (
    isCorrectableEmailRejection(message) ||
    message === EMAIL_VERIFICATION_UNAVAILABLE_MESSAGE
  ) {
    return message
  }

  // Auth gave up waiting for the hook (HTTP 422); nothing is wrong with the address.
  if (code === "hook_timeout") {
    return EMAIL_VERIFICATION_UNAVAILABLE_MESSAGE
  }

  if (code?.startsWith("over_") || error.status === 429) {
    return SIGNUP_RATE_LIMIT_MESSAGE
  }

  if (
    code === "email_address_invalid" ||
    (code === "validation_failed" && normalized.includes("email"))
  ) {
    return EMAIL_DELIVERABILITY_REJECTION_MESSAGE
  }

  // status 0 is supabase-js's AuthRetryableFetchError for a network failure.
  if (error.status === 0 || (error.status ?? 0) >= 500) {
    return SIGNUP_UNAVAILABLE_MESSAGE
  }

  return GENERIC_SIGNUP_ERROR
}
